import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createUUId, exportCSVFile} from "../../../../core/services/utils/utils";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import {PrintPackingListDataGridColumnNames} from "../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes} from "../../../../packages/data-grid";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import NewPackingListDropZone from "../../app-specific/dropzone/new-packing-list-drop-zone";
import Form from "../../base/form";
import {Col, Row} from "reactstrap";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import PartnerDataGrid from "../../../containers/data-grid";
import Button from "../../base/button/button";
import ProductInformationPopover from "../../app-specific/draggable-popover/product-information-popover";

const formNames = {
    dimension: 'dimension',
    weight: 'weight',
    refNo: 'refNo',
    number: 'number',
}
const UpsertPrintPackingListDialog = ({open, close, submit, packingList: packingListProps, total = 0}) => {
    const [initialValues, setInitialValues] = useState({});
    const [packingListData, setPackingListData] = useState(packingListProps?.data);
    const formIdRef = useRef(createUUId());

    const [validate, required] = useMemo(() => {
        const schema = Yup.object().shape({
            [formNames.weight]: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('number')),
            [formNames.dimension]: Yup.string().nullable().required(ValidateMessages.required),
            [formNames.refNo]: Yup.string().nullable().required(ValidateMessages.required),
            [formNames.number]: Yup.number().nullable()
                .required(ValidateMessages.required)
                .typeError(ValidateMessages.incorrectType('number'))
                .integer(ValidateMessages.incorrectType('integer'))
                .moreThan(0, ValidateMessages.greaterThan('0'))
                .lessThan(total + 1 + (!packingListProps ? 1 : 0), ValidateMessages.lessThan((total + 1 + (!packingListProps ? 1 : 0)).toString())),
        })

        const validate = makeValidate(schema);
        const required = makeRequired(schema);
        return [validate, required]
    }, [total]);


    /**
     * Listens to the changes of selectedDriver in the props:
     * With each change, sets the [initialValues] in the state
     */
    useEffect(() => {
        if (!open) {
            setPackingListData(null);
            setInitialValues({});
        }
        setInitialValues({
            [formNames.dimension]: packingListProps?.dimension ?? '',
            [formNames.weight]: packingListProps?.weight ?? '',
            [formNames.refNo]: packingListProps?.refNo ?? '',
            [formNames.number]: packingListProps?.skidNumber ?? '',
        })
        setPackingListData(packingListProps?.data);
    }, [open, packingListProps])


    /**
     * Submits the changes for the packing list.
     * @type {(function(*))}
     */
    const onSubmit = useCallback((values) => {
        submit({
            id: packingListProps?.id ?? createUUId(),
            dimension: values[formNames.dimension],
            weight: Number(values[formNames.weight]),
            refNo: values[formNames.refNo],
            skidNumber: Number(values[formNames.number]),
            data: packingListData,
        })
        close();
    }, [packingListData, packingListProps?.id, submit, close]);

    /**
     * Sets the [packingListData] and [initialValues] in the state when a file is parsed.
     * @type {(function(*): void)}
     */
    const onParse = useCallback((parsedData) => {
        setPackingListData(parsedData.map(e => ({
            partNo: e[0],
            serialNo: e[1],
        })))
        setInitialValues({
            [formNames.dimension]: '',
            [formNames.weight]: '',
            [formNames.refNo]: '',
            [formNames.number]: '',
        })
    }, []);


    const columns = useMemo(() =>
        /** @type {Array<DataGridColumn>}*/
        [
            {
                name: PrintPackingListDataGridColumnNames.index,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                resizable: false,
                reorderable: false,
                width: 0,
            },
            {
                name: PrintPackingListDataGridColumnNames.partNo,
                title: "Part No",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                resizable: false,
                reorderable: false,
            },
            {
                name: PrintPackingListDataGridColumnNames.serialNo,
                title: "Serial No",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                resizable: false,
                reorderable: false,
            }
        ], [])


    /**@type {Array<DataGridRow>}*/
    const rows = useMemo(() => {
        return packingListData?.map((e, index) => ({
            key: index,
            data: e.data,
            cells: {
                [PrintPackingListDataGridColumnNames.index]: index + 1,
                [PrintPackingListDataGridColumnNames.partNo]:
                    <ProductInformationPopover partNo={e?.partNo}>
                        {e.partNo ?? ""}
                    </ProductInformationPopover>,
                [PrintPackingListDataGridColumnNames.serialNo]: e.serialNo,
            }
        })) ?? []
    }, [packingListData])

    /**
     * Creates and exports a sample csv formatted file that has the correct format for this dropzone.
     */
    const downloadSampleFile = () => {
        const entries = Array(10)
            .fill(null)
            .map((_, e) => Object.fromEntries([
                ["Part No", `Part no ${e + 1}`],
                ["Serial No", `Serial no ${e + 1}`],
            ]))
        exportCSVFile(entries, "Packing List Sample File");
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={!packingListData ? 'sm' : 'md'}
                fullWidth
            >
                <DialogTitle>
                    {
                        packingListProps
                            ? "Edit Packing List"
                            : packingListData
                                ? "Save Packing List Information"
                                : "Upload Packing List File"
                    }
                </DialogTitle>
                <DialogContent>
                    {
                        !packingListData &&
                        <NewPackingListDropZone onParsed={onParse}/>
                    }
                    {
                        !!packingListData &&
                        <>
                            <Form
                                className='d-flex flex-wrap w-100 align-items-start mt-4'
                                onSubmit={onSubmit}
                                validate={validate}
                                id={formIdRef.current}
                                initialValues={initialValues}
                                render={({values, form}) => {
                                    return (
                                        <>
                                            <Col xs={12} lg={6} className={'p-0 mb-3 px-2'}>
                                                <OutlinedInputForm
                                                    fullWidth
                                                    name={formNames.number}
                                                    placeholder={'i.e. 1'}
                                                    label={"SKID Number"}
                                                    type={'number'}
                                                    required={required[formNames.number]}
                                                    value={values[formNames.number] ?? ''}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <p
                                                                className={'text-sm secondary-color-1 font-weight-bold text-right'}
                                                                style={{minWidth: 30}}
                                                            >
                                                                / {total + (!packingListProps ? 1 : 0)}
                                                            </p>
                                                        )
                                                    }}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                            </Col>
                                            <Col xs={12} lg={6} className={'p-0 mb-3 px-2'}>
                                                <OutlinedInputForm
                                                    fullWidth
                                                    name={formNames.refNo}
                                                    placeholder={'i.e. 123454'}
                                                    label={"Ref #"}
                                                    required={required[formNames.refNo]}
                                                    value={values[formNames.refNo] ?? ''}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                            </Col>
                                            <Col xs={12} lg={6} className={'p-0 mb-3 px-2'}>
                                                <OutlinedInputForm
                                                    fullWidth
                                                    name={formNames.dimension}
                                                    placeholder={'i.e. 123454'}
                                                    label={"SKID Dimension"}
                                                    required={required[formNames.dimension]}
                                                    value={values[formNames.dimension] ?? ''}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                            </Col>
                                            <Col xs={12} lg={6} className={'p-0 mb-3 px-2'}>
                                                <OutlinedInputForm
                                                    fullWidth
                                                    name={formNames.weight}
                                                    placeholder={'i.e. 123454'}
                                                    label={"Weight (LBs)"}
                                                    required={required[formNames.weight]}
                                                    type={'number'}
                                                    value={values[formNames.weight] ?? ''}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                            </Col>
                                        </>
                                    );
                                }}
                            />
                            <div className={'mt-3'}>
                                <PartnerDataGrid
                                    rows={rows}
                                    columns={columns}
                                    hideFooter
                                    hideToolbar
                                    visibleRows={5}
                                />
                            </div>
                        </>
                    }
                </DialogContent>
                <DialogActions className={'p-0 m-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                color={'primary'}
                                type={'button'}
                                borderRadius={'0 8px 0 8px'}
                                onClick={close}
                            >
                                Cancel
                            </Button>
                            {
                                !packingListData &&
                                <Button
                                    disabled={!open}
                                    variant={'dialog'}
                                    color={'primary'}
                                    type={'button'}
                                    onClick={downloadSampleFile}
                                    borderRadius={'8px 0 8px 0'}
                                >
                                    Download Sample File
                                </Button>
                            }
                            {
                                !!packingListData &&
                                <Button
                                    disabled={!open}
                                    variant={'dialog'}
                                    color={'primary'}
                                    type={'submit'}
                                    form={formIdRef.current}
                                    borderRadius={'8px 0 8px 0'}
                                >
                                    Save
                                </Button>
                            }
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UpsertPrintPackingListDialog;
